import { USER_AUTH_TOKEN_VALIDITY } from '../../../constants/common';

export const LOOKER_AUTH_ERROR_CODE = 'LOOKER_AUTHENTICATION_REQUIRED';

type LookerAuthSession = {
  user_auth_token: string;
  user_auth_token_expiration?: number | string;
  looker_id?: number;
};

type LookerAuthRetryConfig = {
  headers?: any;
  __lookerAuthRetried?: boolean;
};

type LookerAuthResponseError = {
  config?: LookerAuthRetryConfig;
  response?: {
    status?: number;
    data?: {
      data?: {
        error_code?: string;
      };
    };
  };
};

/**
 * ONE shared in-flight refresh of the Looker access token per browser runtime.
 *
 * `restApi.ts` and `axios.ts` each kept their own `isRefreshing` flag, so the
 * two clients could refresh at the same moment. Each refresh re-mints through
 * Looker's `login_user`, and the results race: whichever response lands last
 * writes the cookie, while the other freshly-minted token is already stale.
 * Sharing a single in-flight promise means concurrent callers in this runtime
 * await the same mint instead of triggering competing ones. Separate tabs and
 * server processes have independent runtimes and are intentionally not claimed
 * by this client-side guard.
 *
 * This also removes a deadlock in both previous implementations: when a refresh
 * FAILED, the queued subscribers were never called back, so their promises hung
 * forever. Here every caller shares one promise and they settle together.
 */
let inFlightRefresh: Promise<LookerAuthSession> | null = null;

const writeAuthCookie = (token: string, expiresAt: number) => {
  const expires = new Date(expiresAt).toUTCString();

  if (process.env.NEXT_PUBLIC_HOST_URL === 'http://localhost:3000') {
    document.cookie = `user_auth_token=${token}; expires=${expires}; path=/; SameSite=Strict;`;
    return;
  }

  document.cookie = `user_auth_token=${token}; expires=${expires}; path=/; domain=quattr.com; SameSite=Strict; Secure`;
};

export const refreshLookerAuthSession = (): Promise<LookerAuthSession> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const request = (async () => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const subscriberUserInfo = localStorage.getItem('sub-userInfo');
    const subscriberAuthToken = localStorage.getItem('subscriber-auth-token');
    if (subscriberUserInfo && subscriberAuthToken) {
      headers['subscriber-auth-token'] = subscriberAuthToken;
    }

    const res = await fetch('/api/get-access-token', {
      method: 'GET',
      headers,
    });

    const tokenResponse = (await res.json()) as LookerAuthSession;
    const { user_auth_token, user_auth_token_expiration } = tokenResponse;

    if (!user_auth_token) {
      throw new Error('get-access-token returned no user_auth_token');
    }

    // Keep the cookie and the stored expiry on the SAME clock as the token.
    // The cookie used to be pinned to a hardcoded 55 minutes, so it could
    // outlive the credential it carried and leave a dead token in place with
    // nothing scheduled to replace it.
    const expiresAt = Number(user_auth_token_expiration) || Date.now() + USER_AUTH_TOKEN_VALIDITY;

    localStorage.setItem('user_auth_token_expiration', expiresAt.toString());
    writeAuthCookie(user_auth_token, expiresAt);

    return tokenResponse;
  })();

  inFlightRefresh = request;

  // Clear the slot on BOTH outcomes so a failed refresh can be retried, and
  // handle the derived promise so a rejection here is never unhandled.
  const release = () => {
    if (inFlightRefresh === request) {
      inFlightRefresh = null;
    }
  };
  request.then(release, release);

  return request;
};

export const refreshLookerAuthToken = async (): Promise<string> => {
  const session = await refreshLookerAuthSession();
  return session.user_auth_token;
};

const isMarkedLookerAuthFailure = (error: LookerAuthResponseError) =>
  error?.response?.status === 401 && error?.response?.data?.data?.error_code === LOOKER_AUTH_ERROR_CODE;

/**
 * Recover only from Node's explicit Looker-auth marker. The original request is
 * replayed once with the new token. Unrelated 401s, refresh failures, and a
 * second marked 401 retain the established sign-out behavior.
 */
export const recoverFromLookerAuthError = async <T>(
  error: LookerAuthResponseError,
  retryRequest: (config: LookerAuthRetryConfig) => Promise<T>,
  signOutHandler: () => Promise<unknown>,
  refreshToken: () => Promise<string> = refreshLookerAuthToken
): Promise<T> => {
  const requestConfig = error?.config;

  if (!isMarkedLookerAuthFailure(error) || !requestConfig || requestConfig.__lookerAuthRetried) {
    await signOutHandler();
    return Promise.reject(error);
  }

  requestConfig.__lookerAuthRetried = true;

  let token: string;
  try {
    token = await refreshToken();
  } catch (refreshError) {
    await signOutHandler();
    return Promise.reject(refreshError);
  }

  if (!requestConfig.headers) {
    requestConfig.headers = {};
  }
  if (typeof requestConfig.headers.set === 'function') {
    requestConfig.headers.set('auth_token', token);
  } else {
    requestConfig.headers['auth_token'] = token;
  }

  return retryRequest(requestConfig);
};

/** Test seam: drop any in-flight refresh between cases. */
export const __resetLookerTokenRefreshForTests = () => {
  inFlightRefresh = null;
};
